import { Colors } from '../theme/colors'
import { CoreDocument, Document, parseCoreDocument } from './document'
import { Team } from './team'

export enum EquipmentStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  REMOVED = 'REMOVED',
}

export const EquipmentStatusLabel: Record<
  EquipmentStatus,
  {
    label: string
    background: string
    color: string
    minWidth: number
  }
> = {
  [EquipmentStatus.ACTIVE]: {
    label: 'Active',
    background: Colors.information,
    color: Colors.royalBlue,
    minWidth: 60,
  },
  [EquipmentStatus.INACTIVE]: {
    label: 'Inactive',
    background: Colors.lightGray3,
    color: Colors.midGray,
    minWidth: 70,
  },
  [EquipmentStatus.REMOVED]: {
    label: 'Removed',
    background: Colors.error,
    color: Colors.errorD1,
    minWidth: 80,
  },
}

export enum EquipmentType {
  ALL = 'ALL',
  DRY_VAN = 'DRY_VAN',
  FLATBED = 'FLATBED',
  REEFER = 'REEFER',
  POWER_ONLY = 'POWER_ONLY',
  OTHER = 'OTHER',
  BOX_TRUCK = 'BOX_TRUCK',
  HOT_SHOT = 'HOT_SHOT',
  SPRINTER_VAN = 'SPRINTER_VAN',
  TANKER = 'TANKER',
  HOPPER = 'HOPPER',
  HAZMAT = 'HAZMAT',
  DRAYAGE = 'DRAYAGE',
}

export enum OwnershipType {
  OWN = 'OWN',
  LEASE = 'LEASE',
  RENT = 'RENT',
  LEASE_TO_OWN = 'LEASE_TO_OWN',
  FINANCED = 'FINANCED',
}

export enum EquipmentSource {
  COOP = 'COOP',
  CROSSROADS = 'CROSSROADS',
  OTR = 'OTR',
  PREMIER = 'PREMIER',
  XTRA = 'XTRA',
  OTHER = 'OTHER',
}

export const OwnershipTypeLabel: Record<OwnershipType, string> = {
  [OwnershipType.OWN]: 'Own',
  [OwnershipType.LEASE]: 'Lease',
  [OwnershipType.RENT]: 'Rental program',
  [OwnershipType.LEASE_TO_OWN]: 'CloudTrucks L2O',
  [OwnershipType.FINANCED]: 'Financing',
}

export enum EquipmentColor {
  WHITE = 'WHITE',
  BLACK = 'BLACK',
  GRAY = 'GRAY',
  SILVER = 'SILVER',
  RED = 'RED',
  BLUE = 'BLUE',
  GREEN = 'GREEN',
  ORANGE = 'ORANGE',
  YELLOW = 'YELLOW',
  PURPLE = 'PURPLE',
  OTHER = 'OTHER',
}

export const EquipmentColorLabel: Record<EquipmentColor, string> = {
  [EquipmentColor.WHITE]: 'White',
  [EquipmentColor.BLACK]: 'Black',
  [EquipmentColor.GRAY]: 'Gray',
  [EquipmentColor.SILVER]: 'Silver',
  [EquipmentColor.RED]: 'Red',
  [EquipmentColor.BLUE]: 'Blue',
  [EquipmentColor.GREEN]: 'Green',
  [EquipmentColor.ORANGE]: 'Orange',
  [EquipmentColor.YELLOW]: 'Yellow',
  [EquipmentColor.PURPLE]: 'Purple',
  [EquipmentColor.OTHER]: 'Other',
}

export const EquipmentColorHex: Record<EquipmentColor, [string, string]> = {
  [EquipmentColor.WHITE]: [Colors.white, Colors.midGray],
  [EquipmentColor.BLACK]: [Colors.primaryBlack, Colors.primaryBlack],
  [EquipmentColor.GRAY]: [Colors.lightGray1, Colors.lightGray1],
  [EquipmentColor.SILVER]: ['#C0C0C0', '#C0C0C0'],
  [EquipmentColor.RED]: [Colors.errorD1, Colors.errorD1],
  [EquipmentColor.BLUE]: ['#4A4AFF', '#4A4AFF'],
  [EquipmentColor.GREEN]: [Colors.kellyGreen, Colors.kellyGreen],
  [EquipmentColor.ORANGE]: [Colors.marigoldOrange, Colors.marigoldOrange],
  [EquipmentColor.YELLOW]: [Colors.mangoYellow, '#F0FF40'],
  [EquipmentColor.PURPLE]: ['#613E9B', '#613E9B'],
  [EquipmentColor.OTHER]: ['transparent', 'transparent'],
}

export enum EquipmentVendorStatus {
  TITLE_PENDING = 'TITLE_PENDING',
  SETUP_IN_PROGRESS = 'SETUP_IN_PROGRESS',
  ASSIGNMENT_READY = 'ASSIGNMENT_READY',
  ASSIGNED = 'ASSIGNED',
  IN_SERVICE = 'IN_SERVICE',
}

export const EquipmentVendorStatusLabel: Record<EquipmentVendorStatus, string> = {
  [EquipmentVendorStatus.TITLE_PENDING]: 'Title Pending',
  [EquipmentVendorStatus.SETUP_IN_PROGRESS]: 'Setup in Progress',
  [EquipmentVendorStatus.ASSIGNMENT_READY]: 'Assignment Ready',
  [EquipmentVendorStatus.ASSIGNED]: 'Assigned',
  [EquipmentVendorStatus.IN_SERVICE]: 'In Service',
}

export const EquipmentVendorStatusColorHex: Record<EquipmentVendorStatus, [string, string]> = {
  [EquipmentVendorStatus.TITLE_PENDING]: [Colors.errorD1, Colors.error],
  [EquipmentVendorStatus.SETUP_IN_PROGRESS]: [Colors.marigoldOrange, Colors.warning],
  [EquipmentVendorStatus.ASSIGNMENT_READY]: [Colors.kellyGreenD1, Colors.success],
  [EquipmentVendorStatus.ASSIGNED]: [Colors.royalBlue, Colors.information],
  [EquipmentVendorStatus.IN_SERVICE]: [Colors.royalBlue, Colors.information],
}

/* eslint-disable-next-line @typescript-eslint/naming-convention */
export interface LastDotInspectionDocument extends CoreDocument<{ LAST_DOT_INSPECTION_DATE: string }> {}

export interface Equipment {
  id: string
  team?: Team
  type?: string
  status?: EquipmentStatus
  dateUpdated?: string
  vehicleYear?: number
  vehicleMake?: string
  vehicleModel?: string
  vehicleColor?: EquipmentColor
  vinNumber?: string
  licensePlateNumber?: string
  licensePlateState?: string
  ownershipType?: OwnershipType
  vehicleRegistration?: string
  vehicleRegistrationDocument?: CoreDocument<{}> | null
  vehicleRegistrationExpiration?: string
  vehicleNumber?: string
  lastDotInspection?: string
  dateLastDotInspection?: string
  dateLastDotInspectionExpired?: string
  lastDotInspectionDocument?: LastDotInspectionDocument | null
  dateVehicleConfirmed?: string
  insurancePhysicalDamage?: string
  insurancePhysicalDamageDocument?: CoreDocument<{}> | null
  insurancePhysicalDamageExpiration?: string
  insuranceNonTruckingLiability?: string
  insuranceNonTruckingLiabilityDocument?: CoreDocument<{}> | null
  insuranceNonTruckingLiabilityExpiration?: string
  notes?: string
  vendorName?: string
  vendorStatus?: EquipmentVendorStatus
  dealership?: string
  pickupLocation?: string
  drivers?: Array<{ id: string; fullName: string }>
  source: EquipmentSource
}

export interface EquipmentPayload {
  id?: string
  status?: EquipmentStatus
  date_updated?: string
  formData?: FormData
  vehicle_year?: number
  vehicle_make?: string
  vehicle_model?: string
  vehicle_number?: string
  vehicle_color?: EquipmentColor
  vin_number?: string
  license_plate_number?: string
  license_plate_state?: string
  ownership_type?: OwnershipType
  vehicle_registration?: string
  vehicle_registration_expiration?: string
  vehicle_registration_document?: Document<{ license_plate_number: string; license_plate_state: string }>
  last_dot_inspection?: string
  date_last_dot_inspection?: string
  date_last_dot_inspection_expired?: string
  last_dot_inspection_document?: Document<{
    LAST_DOT_INSPECTION_DATE: string
    license_plate_number: string
    license_plate_state: string
    date_created: string
  }>
  date_vehicle_confirmed?: string
  insurance_physical_damage?: string
  insurance_physical_damage_document?: Document<{ license_plate_number: string; license_plate_state: string }>
  insurance_physical_damage_expiration?: string
  insurance_non_trucking_liability?: string
  insurance_non_trucking_liability_document?: Document<{
    license_plate_number: string
    license_plate_state: string
  }>
  insurance_non_trucking_liability_expiration?: string
  internal_notes?: string
  vendor_name?: string
  vendor_status?: EquipmentVendorStatus
  dealership?: string
  pickup_location?: string
  source?: EquipmentSource
}

export function parseEquipment(equipment: EquipmentPayload): Equipment {
  if (!equipment.id) {
    throw new Error('Equipment id is required')
  }
  return {
    id: equipment.id,
    status: equipment.status,
    dateUpdated: equipment.date_updated,
    vehicleYear: equipment.vehicle_year,
    vehicleMake: equipment.vehicle_make,
    vehicleModel: equipment.vehicle_model,
    vehicleNumber: equipment.vehicle_number,
    vehicleColor: equipment.vehicle_color,
    dealership: equipment.dealership,
    pickupLocation: equipment.pickup_location,
    vinNumber: equipment.vin_number,
    licensePlateNumber: equipment.license_plate_number,
    licensePlateState: equipment.license_plate_state,
    ownershipType: equipment.ownership_type,
    vehicleRegistration: equipment.vehicle_registration,
    vehicleRegistrationDocument: parseCoreDocument(equipment.vehicle_registration_document),
    vehicleRegistrationExpiration: equipment.vehicle_registration_expiration,
    lastDotInspection: equipment.last_dot_inspection,
    lastDotInspectionDocument: parseCoreDocument(equipment.last_dot_inspection_document),
    dateLastDotInspection: equipment.date_last_dot_inspection,
    dateLastDotInspectionExpired: equipment.date_last_dot_inspection_expired,
    dateVehicleConfirmed: equipment.date_vehicle_confirmed,
    insurancePhysicalDamage: equipment.insurance_physical_damage,
    insurancePhysicalDamageDocument: parseCoreDocument(equipment.insurance_physical_damage_document),
    insurancePhysicalDamageExpiration: equipment.insurance_physical_damage_expiration,
    insuranceNonTruckingLiability: equipment.insurance_non_trucking_liability,
    insuranceNonTruckingLiabilityDocument: parseCoreDocument(
      equipment.insurance_non_trucking_liability_document
    ),
    insuranceNonTruckingLiabilityExpiration: equipment.insurance_non_trucking_liability_expiration,
    notes: equipment.internal_notes,
    source: equipment.source || EquipmentSource.OTHER,
  }
}
